/**
 * Image Alt Text Utilities
 * 
 * Provides helper functions for generating SEO-optimized, accessible image alt text.
 * Follows WCAG 2.1 Level A Success Criterion 1.1.1 (Non-text Content)
 * 
 * Alt Text Formula: "[Product Name] - [Key Visual Feature] - [Material/Style]"
 */

import { ShopifyProduct, ShopifyVariant, ShopifyImage } from '@/hooks/useShopify';

/**
 * Generate enhanced alt text for product images
 * Uses Shopify's alt text if available, otherwise constructs descriptive alt
 * 
 * @param product - The Shopify product
 * @param currentImage - The specific image to describe
 * @param variant - Optional variant for additional context
 * @returns Descriptive alt text string
 */
export const getEnhancedProductAlt = (
  product: ShopifyProduct,
  currentImage?: ShopifyImage | null,
  variant?: ShopifyVariant | null
): string => {
  // Use Shopify's alt text if available (merchant-curated)
  if (currentImage?.altText) {
    return currentImage.altText;
  }
  
  // Build enhanced alt text from available product data
  const parts: string[] = [product.title];
  
  // Add variant details if meaningful (not default/generic)
  if (variant?.title && 
      variant.title !== 'Default Title' && 
      !variant.selectedOptions?.some(opt => 
        opt.name === 'Title' || opt.value === 'Default Title'
      )) {
    parts.push(variant.title);
  }
  
  // Add product type for semantic context
  if (product.productType && product.productType.toLowerCase() !== 'default') {
    parts.push(product.productType.toLowerCase());
  }
  
  return parts.join(' - ');
};

/**
 * Generate hero section ARIA label
 * Describes the visual scene for screen readers when using CSS background images
 * 
 * @param scene - Description of the visual scene
 * @param theme - Optional thematic descriptor
 * @returns Accessible ARIA label
 */
export const getHeroAriaLabel = (scene: string, theme?: string): string => {
  const base = `Hero banner featuring ${scene}`;
  return theme ? `${base} in a ${theme} style` : base;
};

/**
 * Generate collection hero ARIA label
 * Dynamic label based on collection theme and mood
 * 
 * @param collectionTitle - Name of the collection
 * @param mood - Mood/atmosphere of the collection (e.g., "joyful", "edgy", "serene")
 * @param style - Style descriptor (defaults to "bohemian")
 * @returns Accessible ARIA label
 */
export const getCollectionHeroAriaLabel = (
  collectionTitle: string,
  mood?: string,
  style: string = 'bohemian'
): string => {
  const moodText = mood ? ` showcasing ${mood}` : '';
  return `${collectionTitle} collection hero${moodText} ${style} style pieces`;
};

/**
 * Validate alt text quality
 * Checks for common issues in alt text
 * 
 * @param altText - The alt text to validate
 * @returns Object with validation result and suggestions
 */
export const validateAltText = (altText: string): {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
} => {
  const issues: string[] = [];
  const suggestions: string[] = [];
  
  // Check for empty or missing alt text
  if (!altText || altText.trim() === '') {
    issues.push('Alt text is empty');
    suggestions.push('Provide descriptive alt text for the image');
  }
  
  // Check for generic patterns
  const genericPatterns = [
    /^image$/i,
    /^photo$/i,
    /^picture$/i,
    /^img$/i,
    /\.jpg$/i,
    /\.png$/i,
    /\.webp$/i
  ];
  
  if (genericPatterns.some(pattern => pattern.test(altText))) {
    issues.push('Alt text is too generic or contains file extension');
    suggestions.push('Use descriptive text that explains what the image shows');
  }
  
  // Check length (too short)
  if (altText.length < 10) {
    issues.push('Alt text is very short');
    suggestions.push('Add more descriptive details (visual features, materials, style)');
  }
  
  // Check length (too long - screen readers may truncate)
  if (altText.length > 125) {
    issues.push('Alt text exceeds recommended length (125 characters)');
    suggestions.push('Consider making description more concise while keeping key details');
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    suggestions
  };
};

/**
 * Get blog image alt text with proper fallback
 * Prioritizes custom alt_text field, falls back to descriptive pattern
 * 
 * @param customAlt - Custom alt text from database
 * @param postTitle - Blog post title for fallback
 * @param context - Context of image usage (e.g., "featured", "thumbnail", "cover")
 * @returns Descriptive alt text
 */
export const getBlogImageAlt = (
  customAlt: string | undefined | null,
  postTitle: string,
  context: 'featured' | 'thumbnail' | 'cover' = 'featured'
): string => {
  if (customAlt) {
    return customAlt;
  }
  
  const contextMap = {
    featured: 'Featured image',
    thumbnail: 'Thumbnail',
    cover: 'Cover image'
  };
  
  return `${contextMap[context]} for blog post: ${postTitle}`;
};
